// Le pagine di una bolletta fotografate e non ancora mandate, salvate sul telefono. Su Android aprire la fotocamera
// può far scartare la scheda del browser per memoria: al ritorno la pagina riparte da zero, e le pagine già scattate
// riappaiono da qui. Sono immagini: IndexedDB, non localStorage. Ogni lettura e scrittura è protetta: senza IndexedDB
// (navigazione privata, browser vecchi) leggere dà nessuna pagina e scrivere non fa nulla, e si lavora in memoria.

const DB = "preventivo-foto";
const STORE = "pagine";

function apri(): Promise<IDBDatabase | null> {
  return new Promise((ok) => {
    try {
      const r = indexedDB.open(DB, 1);
      r.onupgradeneeded = () => r.result.createObjectStore(STORE);
      r.onsuccess = () => ok(r.result);
      r.onerror = () => ok(null);
      r.onblocked = () => ok(null);
    } catch {
      ok(null);
    }
  });
}

/** Una richiesta sullo store, in una transazione sua: → il risultato, o `undefined` se qualcosa non va. */
async function richiesta(modo: IDBTransactionMode, fa: (s: IDBObjectStore) => IDBRequest): Promise<unknown> {
  const db = await apri();
  if (!db) return undefined;
  return new Promise((ok) => {
    try {
      const tx = db.transaction(STORE, modo);
      const r = fa(tx.objectStore(STORE));
      tx.oncomplete = () => { db.close(); ok(r.result); };
      tx.onerror = () => { db.close(); ok(undefined); };
      tx.onabort = () => { db.close(); ok(undefined); };
    } catch {
      db.close();
      ok(undefined);
    }
  });
}

/** Le pagine salvate per `chiave` (l'utente), nell'ordine; nessuna se non ce ne sono o non si possono leggere. */
export async function leggiPagine(chiave: string): Promise<Blob[]> {
  const v = await richiesta("readonly", (s) => s.get(chiave));
  return Array.isArray(v) ? v.filter((p): p is Blob => p instanceof Blob) : [];
}

export async function salvaPagine(chiave: string, pagine: Blob[]): Promise<void> {
  await richiesta("readwrite", (s) => s.put(pagine, chiave));
}

export async function cancellaPagine(chiave: string): Promise<void> {
  await richiesta("readwrite", (s) => s.delete(chiave));
}

/** Una bolletta finita che aspetta di partire: le sue pagine e l'identificativo con cui parte (lo stesso a ogni tentativo). */
export interface InCoda {
  id: string;
  pagine: Blob[];
  /** «Rifai la foto»: la bolletta vecchia che si toglie quando questa è arrivata. */
  sostituisce?: string;
}

/** La coda d'invio salvata per `chiave`, nell'ordine di partenza. */
export async function leggiCoda(chiave: string): Promise<InCoda[]> {
  const v = await richiesta("readonly", (s) => s.get(chiave));
  if (!Array.isArray(v)) return [];
  return v.filter((x): x is InCoda => typeof x?.id === "string" && Array.isArray(x.pagine) && x.pagine.every((p: unknown) => p instanceof Blob));
}

export async function salvaCoda(chiave: string, coda: InCoda[]): Promise<void> {
  await richiesta("readwrite", (s) => (coda.length > 0 ? s.put(coda, chiave) : s.delete(chiave)));
}
